import mixpanel from '@disco/api/mixpanel'
import captureError from '@disco/utils/captureError'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'

import type { Track } from 'types/track'

export const OPEN_RIGHTS_TAB_EVENT = 'Open rights tab'

export type RightsTypeLabel = 'Publishing' | 'Recording'
export type AvailableRightsTypes = RightsTypeLabel | 'Both'

/** The parts of a track the MDS event properties are read from */
export type MdsEventTrack = Pick<Track, 'internal_data' | 'origin'>

export interface OpenRightsTabArgs {
  rightsType: RightsTypeLabel
  trigger: 'Default' | 'Switch'
  availableRightsTypes: AvailableRightsTypes
  /** Original track, used for the MDS catalog properties */
  track?: MdsEventTrack | null
  trackId?: number | null
  /** Set for bulk edit, where splits aren't loaded */
  bulkTrackCount?: number
  hasData: boolean
  splitCount: number
}

export const buildOpenRightsTabProps = ({
  rightsType,
  trigger,
  availableRightsTypes,
  track,
  trackId,
  bulkTrackCount,
  hasData,
  splitCount,
}: OpenRightsTabArgs): Record<string, unknown> => {
  const isBulk = bulkTrackCount !== undefined
  return {
    'Rights type': rightsType,
    Trigger: trigger,
    'Available rights types': availableRightsTypes,
    ...(isBulk
      ? { Bulk: true, 'Track count': bulkTrackCount }
      : {
          'Track ID': trackId,
          'Has data': hasData,
          'Split count': splitCount,
        }),
    // Matches the DISCO-5985 properties on the other MDS events
    ...(isAggregatedSearchTrack(track)
      ? {
          Context: 'Aggregated search',
          Catalog: track?.origin?.source_library?.id,
          'Catalog Business': track?.origin?.business?.id,
        }
      : {}),
  }
}

/** Never throws: a tracking failure must not break the Rights tab. */
export const sendOpenRightsTabEvent = (args: OpenRightsTabArgs): void => {
  try {
    mixpanel.sendPureEvent(OPEN_RIGHTS_TAB_EVENT, buildOpenRightsTabProps(args))
  } catch (error) {
    captureError(error as Error, { moduleName: 'RightsMixpanel' })
  }
}
